import { startGame } from '@sl/base/main';
import { Scene } from '@sl/base/scene';
import { Bonus } from '@sl/games/miner/bonus';
import { Payline } from '@sl/base/logic/payline';
import { Winning } from '@sl/base/logic/winning';

class GameScene extends Scene {
    update() {
        super.update();
    }

    init() {
        super.init();

        this.letInitialize(() => {
            this.winTable = undefined;
            this.winText = undefined;
            this.amountWonStep = 15;
            this.amountWonTimeout = 40;
            this.amountWonFadeInSpeed = 300;

            this.positionerContainer = this.add.container();
            this.createBg();

            this.slots.symbolHeight = 160;
            this.slots.symbolWidth = 160;
            this.slots.spinningRowsCount = 6;
            this.slots.symbolsOffsetLeft = 100;
            this.slots.visibleRowsCount = 3;
            this.slots.frameMaskOffset = 25;
            this.slots.frameMaskHeight = 468;
            this.slots.reelsOffsetTop = 339;
            this.slots.symbolBlurFrame = 25;
            this.slots.spaceBetweenReels = -5;

            this.slots.createSlotMachine();
            this.addGameLogo();
            this.addSymbolsAnimations();

            this.bonus = new Bonus(this);
            this.payline = new Payline(this);
            this.winning = new Winning(this);

            this.payline.drawPaylines();
            
            this.addCharacter();

            this.spinSound = this.sound.add('spin', {"loop":false, "volume": 1});

            this.createWinningsContainer();
            this.spritesReady = true;
            this.baseGamePositioner();
        });
    }

    afterPurchase() {
        this.winning.removeTimers();
        this.slots.spin();
        this.character.lamp.alpha = 1;
    }

    afterStep() {
        const { scenario } = this.request;

        if (scenario) {
            if(!this.skip && scenario.freespins && parseInt(scenario.freespins.current) === 1) {
                this.bonus.init(() => {
                    this.afterPurchase();
                });
            } else {
                if (scenario.reels) {
                    this.afterPurchase();
                } 
            }

            return;
        }

        this.request.spinFinished();
        this.character.lamp.alpha = 0;
    }

    createWinningsContainer() {
        this.winningsContainer = this.add.container();
        this.winningsContainer.alpha = 0;

        let sprite = this.add.sprite(this.slots.frame.x, this.slots.frame.y, 'popup').setOrigin(0.5, 0.5);
        sprite.name = 'popup';
        this.winningsContainer.add(sprite);

        let winAmount = this.add.bitmapText(this.slots.frame.x, this.slots.frame.y, 'winning_font', 0, 60).setOrigin(0.5, 0.5);
        winAmount.name = 'win_amount';
        this.winningsContainer.add(winAmount);
    }

    createBg() {
        this.bg = this.add.sprite(0, 0, 'bg').setOrigin(0.5, 0);
        this.positionerContainer.add(this.bg);
    }

    addGameLogo() {
        this.logo = this.add.sprite(0, 0, 'logo').setOrigin(0.5, 0.5);
    }

    addCharacter() {
        this.characterOverlay = this.add.graphics({ fillStyle:{ color: 0x000000 } });
        var rect = new Phaser.Geom.Rectangle(-810, 0, 1620, 1620);
        this.characterOverlay.fillRectShape(rect);
        this.characterOverlay.alpha = 0;
        this.character = this.add.sprite(this.slots.frame.x - this.slots.frame.width / 2 - 250, 90, 'character').setOrigin(0, 0);
        this.character.lamp = this.add.sprite(0, 0, 'miner_lamp').setOrigin(0, 0);
        this.character.lamp.alpha = 0;
        this.character.bubble = this.add.sprite(this.character.width + this.character.x - 70, this.character.y - 50, 'bubble').setOrigin(0, 0);
        this.character.bubble.alpha = 0;
        this.character.text = this.add.text(this.character.bubble.x + 80, this.character.bubble.y + 80, 'Welcome to \n the mancave', { color: '#000000'});
        this.character.text.alpha = 0;
        this.positionerContainer.add(this.character);
        this.bonus.bonusContainer.add(this.characterOverlay);
        this.bonus.bonusContainer.add(this.character.bubble);
        this.bonus.bonusContainer.add(this.character.text);
    }

    afterSpin() {
        this.request.step();
    }

    addSymbolsAnimations() {
        for(const [ reelIndex, reel ] of Object.entries(this.slots.reelsContainersObjects)) {
            for(const symbolIndex of Object.keys(reel.symbols)) {
                let symbol = reel.symbols[symbolIndex];
                var config = {
                    key: `${symbol.texture.key}`,
                    frames: this.anims.generateFrameNumbers(`${symbol.texture.key}`, {
                        start: 0,
                        end: 24,
                    }),
                    frameRate: 20,
                    yoyo: 0,
                    repeat: 0
                };
            
                this.anims.create(config);
                symbol.anims.load(`${symbol.texture.key}`);
            }
        }
    }

    showWinning(text, callback) {
        let win = 'Mega';
        let sound = 'Mega';

        if(!text) {
            var winningAmount = this.request.scenario.amount_won;

            if(!winningAmount) {
                this.winning.playPaylines();

                if(callback) {
                    callback();
                }

                return;
            }
            
            var currentAmount = 0;

            win = 'Regular';
            sound = 'Big';

            if(this.request.scenario.tags.big_win) {
                win = 'Big';
            }
        }

        this.prepareWinningContainer(win);

        this.winningsContainer.tween = this.tweens.add({
            targets: [this.winningsContainer],
            alpha: 1,
            ease: 'Power0',
            duration: 200,
            yoyo: 0,
            repeat: 0,
        });

        this.winningsContainer.getByName('win_amount').setText(text ? text : 0);
        
        this.sound.play(sound);

        const tween = this.tweens.add({
            targets: [this.winningsContainer.getByName('win_amount')],
            alpha: 1,
            ease: 'Power0',
            duration: this.amountWonFadeInSpeed,
            yoyo: 0,
            repeat: 0,
            onComplete: () => {
                if(!text) {
                    let timeout = 0;
                    let step = winningAmount / this.amountWonStep;
                    let last = false;

                    if(win === 'Big') {
                        step = winningAmount / (this.amountWonStep * 3);
                    }
    
                    while(currentAmount < winningAmount) {
                        currentAmount += step;
    
                        if(currentAmount > winningAmount) {
                            currentAmount = winningAmount;
                        }
    
                        if(currentAmount === winningAmount) {
                            last = true;
                        }
    
                        let timer = this.time.delayedCall(timeout, this.setAmountText, [currentAmount, last, false, false], this);
    
                        this.winning.timers.push(timer);
                        timeout += this.amountWonTimeout;
                    }
                } else {
                    let timer = this.time.delayedCall(0, this.setAmountText, [text, true, callback, true], this);
                    this.winning.timers.push(timer);
                }
            },
        });
        
        this.winningsContainer.getByName('win_amount').tween = tween;
        this.winning.tweenTimers.push(tween);
    }

    setAmountText(amount, last, callback, string) {
        if(!string) {
            amount = parseFloat(amount).toFixed(Config.currency.precision);
            this.sound.play('coin');
        }

        this.winningsContainer.getByName('win_amount').setText(amount);

        if(last) {
            const tween = this.tweens.add({
                targets: [this.winningsContainer],
                alpha: 0,
                ease: 'Power0',
                duration: this.amountWonFadeInSpeed,
                yoyo: 0,
                repeat: 0,
                delay: 4 * this.amountWonFadeInSpeed,
                onComplete: () => {
                    this.winning.playPaylines();

                    if(callback) {
                        callback();
                    }
                },
            });

            this.winning.tweenTimers.push(tween);
        }
    }

    prepareWinningContainer(win) {
        this.hideWinTable();

        this.winningsContainer.alpha = 0;

        this.winningsContainer.getByName('win_amount').alpha = 0;
    }

    hideWinTable() {
        if(this.winningsContainer && this.winningsContainer.alpha) {
            if(this.winningsContainer.tween && this.winningsContainer.tween.isPlaying()) {
                this.winningsContainer.tween.stop();
                this.winningsContainer.tween.remove();
            }
        
            this.winningsContainer.alpha = 0;

            for(const [symbolIndex, sprite] of Object.entries(this.winningsContainer.getAll())) {
                if(sprite.tween && sprite.tween.isPlaying()) {
                    sprite.tween.stop();
                    sprite.tween.remove();
                }
            }
        }
    }

    playSpinSound() {
        this.spinSound.play();
    }

    muteSpinSound() {
        this.spinSound.stop();
    }

    playStopSound() {
        this.sound.play('click', {"volume":0.4});
    }

    playWinlineSound() {
        this.sound.play('gem');
    }

    skipAnimations() {
        this.winning.removeTimers();
        this.hideWinTable();
        this.skip = true;
        this.winning.doCallback();
    }

    gamePositioner() {
        if(!this.spritesReady) {
            return;
        }

        this.logo.x = this.slots.frame.x + this.cameras.main.centerX;
        this.logo.y = 95 + this.slots.frameTopOffset;

        if(this.orientationIsSetToPortrait()) {
            this.bg.setTexture('bg_portrait');
            this.character.x = this.slots.frame.x - this.slots.frame.width / 2 - 50;
            this.character.y = 800;
            this.character.lamp.x = -10;
        } else {
            this.bg.setTexture('bg');
            this.character.x = this.slots.frame.x - this.slots.frame.width / 2 - 250;
            this.character.lamp.x = 20;
            this.character.y = 90;
        }
        this.character.lamp.y = this.character.y;
        this.character.bubble.x = this.character.width + this.character.x - 70;
        this.character.bubble.y = this.character.y - 50;
        this.character.text.x = this.character.bubble.x + 80; 
        this.character.text.y = this.character.bubble.y + 80;
    }
}

startGame(GameScene);